import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["assets"],
  _excluded2 = ["refetch"];
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'data-fetching-client';
import { GET_PORTABLE_KEYS } from '../../data/queries';
import { isWorkflowAsset } from '../../types';
import { captureException } from '../../utils/captureException';
export function usePortableKeys(options = {}) {
  var _queryOptions$variabl2, _queryOptions$skip;
  const {
      assets
    } = options,
    queryOptions = _objectWithoutPropertiesLoose(options, _excluded);
  const [portableKeyMap, setPortableKeyMap] = useState({});

  // For workflows, extract portable keys from metadata instead of fetching from API
  const workflowPortableKeyMap = useMemo(() => {
    var _queryOptions$variabl;
    if (!assets || ((_queryOptions$variabl = queryOptions.variables) === null || _queryOptions$variabl === void 0 ? void 0 : _queryOptions$variabl.portableType) !== 'workflows') {
      return null;
    }
    const map = {};
    assets.forEach(asset => {
      if (isWorkflowAsset(asset)) {
        const portableKey = asset.metadata.workflows.portableKey;
        if (portableKey) {
          map[asset.assetId.toString()] = portableKey;
        }
      }
    });
    return map;
  }, [assets, (_queryOptions$variabl2 = queryOptions.variables) === null || _queryOptions$variabl2 === void 0 ? void 0 : _queryOptions$variabl2.portableType]);

  // If workflow portable keys are extracted, use them instead of fetching
  useEffect(() => {
    if (workflowPortableKeyMap) {
      setPortableKeyMap(workflowPortableKeyMap);
    }
  }, [workflowPortableKeyMap]);
  const _useQuery = useQuery(GET_PORTABLE_KEYS, Object.assign({}, queryOptions, {
      skip: (_queryOptions$skip = queryOptions.skip) !== null && _queryOptions$skip !== void 0 ? _queryOptions$skip : !!workflowPortableKeyMap,
      onError: error => {
        var _queryOptions$onError;
        captureException(error, ['usePortableKeys']);
        (_queryOptions$onError = queryOptions.onError) === null || _queryOptions$onError === void 0 || _queryOptions$onError.call(queryOptions, error);
      },
      onCompleted: data => {
        var _data$portableKeys, _queryOptions$onCompl;
        if (data !== null && data !== void 0 && (_data$portableKeys = data.portableKeys) !== null && _data$portableKeys !== void 0 && _data$portableKeys.assetIdsToPortableKeys) {
          setPortableKeyMap(data.portableKeys.assetIdsToPortableKeys);
        }
        (_queryOptions$onCompl = queryOptions.onCompleted) === null || _queryOptions$onCompl === void 0 || _queryOptions$onCompl.call(queryOptions, data);
      }
    })),
    {
      refetch
    } = _useQuery,
    portableKeysQuery = _objectWithoutPropertiesLoose(_useQuery, _excluded2);
  const fetchPortableKeys = useCallback(async () => {
    // If we have workflow keys extracted from metadata, return them directly
    if (workflowPortableKeyMap) {
      return Object.values(workflowPortableKeyMap);
    }
    const response = await refetch();
    if (!response.data) {
      return [];
    }
    const {
      assetIdsToPortableKeys
    } = response.data.portableKeys;
    return Object.values(assetIdsToPortableKeys);
  }, [refetch, workflowPortableKeyMap]);
  const allPortableKeys = useMemo(() => {
    return Object.values(portableKeyMap);
  }, [portableKeyMap]);
  return Object.assign({
    allPortableKeys,
    fetchPortableKeys,
    portableKeyMap
  }, portableKeysQuery);
}